/** @jsxImportSource theme-ui */
import React, { useEffect } from 'react';
import { Box, Button, Text, Flex } from '@infotrack/zenith-ui';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'warning' | 'danger' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  // Handle ESC key to close dialog
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && onCancel) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onCancel]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const variantConfig = {
    warning: {
      iconBg: 'rgba(251, 191, 36, 0.15)',
      iconColor: '#f59e0b',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      accentColor: '#f59e0b',
    },
    danger: {
      iconBg: 'rgba(239, 68, 68, 0.15)',
      iconColor: '#ef4444',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      accentColor: '#ef4444',
    },
    info: {
      iconBg: 'rgba(59, 130, 246, 0.15)',
      iconColor: '#3b82f6',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ),
      accentColor: '#3b82f6',
    },
  };

  const config = variantConfig[variant];

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onCancel}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
          animation: 'fadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: onCancel ? 'pointer' : 'default',
          '@keyframes fadeIn': {
            from: { 
              opacity: 0,
              backdropFilter: 'blur(0px)',
            },
            to: { 
              opacity: 1,
              backdropFilter: 'blur(4px)',
            },
          },
        }}
      />

      {/* Dialog */}
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '480px',
          width: 'calc(100% - 32px)',
          zIndex: 9999,
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden',
          '@keyframes slideUp': {
            from: {
              opacity: 0,
              transform: 'translate(-50%, -48%) scale(0.96)',
            },
            to: {
              opacity: 1,
              transform: 'translate(-50%, -50%) scale(1)',
            },
          },
        }}
      >
        {/* Accent Border */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${config.accentColor}, ${config.accentColor}dd)`,
          }}
        />

        {/* Content */}
        <Flex
          sx={{
            padding: '32px 24px 24px',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '20px',
          }}
        >
          {/* Icon */}
          <Flex
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: config.iconBg,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: config.iconColor,
              boxShadow: `0 0 0 8px ${config.iconBg}50`,
            }}
          >
            {config.icon}
          </Flex>

          {/* Title */}
          <Box id="dialog-title" sx={{ width: '100%' }}>
            <Text variant="title" as="h2">
              {title}
            </Text>
          </Box>

          {/* Message */}
          <Box 
            id="dialog-message" 
            sx={{ 
              width: '100%',
              color: '#64748b',
              lineHeight: '1.6',
            }}
          >
            <Text variant="medium">
              {message}
            </Text>
          </Box>
        </Flex>

        {/* Buttons */}
        <Flex
          sx={{
            padding: '16px 24px 24px',
            gap: '12px',
            justifyContent: 'center',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          {onCancel && (
            <Box>
              <Button
                onClick={onCancel}
                variant="secondary"
              >
                {cancelText}
              </Button>
            </Box>
          )}
          <Box>
            <Button
              onClick={onConfirm}
              variant="primary"
            >
              {confirmText}
            </Button>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
